import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import SeoHelmet from '../../common/SeoHelmet';
const Vs30TheoryEC8 = () => {
  return (
    <>
      <SeoHelmet
        title="GeoCalc | VS₃₀ & EC8 Site Classification | Theory"
        description="Definition of VS₃₀, harmonic-mean formula, and Eurocode 8 ground types (A–E, S1–S2). GeoCalc classifies solely by VS₃₀; NSPT and cu ranges shown for reference."
        url="https://www.geocalc.app/vs30-ec8-theory"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="page"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'VS₃₀ & EC8 | Theory', url: 'https://www.geocalc.app/vs30-ec8-theory' },
        ]}
      />

      <Header />
      <div className="theory">
        {/* Light/Dark-friendly table borders */}
        <style>{`
          .theory .table.bordered {
            border-collapse: collapse;
            width: 100%;
          }
          .theory .table.bordered th,
          .theory .table.bordered td {
            border: 1px solid rgba(128,128,128,0.35);
            padding: 8px 10px;
          }
          .theory .table.bordered thead th {
            background: rgba(128,128,128,0.12);
          }
          .theory .table.bordered {
          border-collapse: collapse;
          width: 100%;
          }
          .theory .table.bordered th,
          .theory .table.bordered td {
            border: 1px solid rgba(128,128,128,0.35);
            padding: 8px 10px;
          }
          .theory .table.bordered thead th {
            background: rgba(128,128,128,0.12);
          }

          /* Adjust column widths */
          .ec8GroundTypes th:nth-child(4),
          .ec8GroundTypes td:nth-child(4) {
            width: 110px; /* narrower N-SPT column */
            text-align: center;
          }

          .ec8GroundTypes th:nth-child(5),
          .ec8GroundTypes td:nth-child(5) {
            width: 150px; /* slightly wider cu column */
            text-align: center;
          }
        `}</style>

        <div className="theoryContainer vs30Theory uscsClassifierTheory">
          <h1>VS₃₀ &amp; Eurocode 8 Site Classification (EN 1998-1)</h1>

          <h2>Purpose</h2>
          <p>
            This page documents the definitions and EC8 ground-type table used by GeoCalc’s VS₃₀ calculator and site
            classification (A–E, with special cases S<sub>1</sub>/S<sub>2</sub>).
          </p>

          <h2>Definition of VS₃₀</h2>
          <p>
            VS₃₀ is the average shear-wave velocity in the upper 30&nbsp;m, computed in accordance with the following expression:{' '}
          </p>

          <h3>Harmonic-mean formula</h3>
          <math display="block">
            <msub>
              <mi>v</mi>
              <mrow>
                <mi>s</mi>
                <mo>,</mo>
                <mn>30</mn>
              </mrow>
            </msub>
            <mo>=</mo>
            <mfrac>
              <mn>30</mn>
              <mrow>
                <munder>
                  <mo>&#8721;</mo>
                  <mrow>
                    <mi>i</mi>
                    <mo>=</mo>
                    <mn>1</mn>
                    <mo>,</mo>
                    <mi>N</mi>
                  </mrow>
                </munder>
                <mfrac>
                  <msub>
                    <mi>h</mi>
                    <mi>i</mi>
                  </msub>
                  <msub>
                    <mi>v</mi>
                    <mi>i</mi>
                  </msub>
                </mfrac>
              </mrow>
            </mfrac>
          </math>

          <h3>Depth constraint</h3>
          <math display="block">
            <mrow>
              <mo>&#8721;</mo>
              <msub>
                <mi>h</mi>
                <mi>i</mi>
              </msub>
              <mo>=</mo>
              <mn>30</mn>
              <mi> m</mi>
            </mrow>
          </math>

          <h3>Layer symbols</h3>
          <math display="block">
            <msub>
              <mi>h</mi>
              <mi>i</mi>
            </msub>
            <mo>:</mo>
            <mtext>&nbsp;thickness of layer&nbsp;</mtext>
            <mi>i</mi>
            <mtext>&nbsp;(m)</mtext>
          </math>
          <math display="block">
            <msub>
              <mi>v</mi>
              <mi>i</mi>
            </msub>
            <mo>:</mo>
            <mtext>&nbsp;shear-wave velocity of layer&nbsp;</mtext>
            <mi>i</mi>
            <mtext>&nbsp;(m/s)</mtext>
          </math>

          <h2>EC8 Ground Types (Table 3.1)</h2>
          <p>
            Classification in GeoCalc is determined solely from VS₃₀ values in accordance with Eurocode 8 (EN 1998-1). N
            <sub>SPT</sub> and c<sub>u</sub> ranges are shown only for reference and are not used in the automated classification.
          </p>

          <div className="tableWrapper" style={{ overflowX: 'auto' }}>
            <table className="table bordered ec8GroundTypes">
              <thead>
                <tr>
                  <th>Ground type</th>
                  <th>Description of stratigraphic profile</th>
                  <th>VS₃₀ (m/s)</th>
                  <th>
                    N<sub>SPT</sub> (blows/30&nbsp;cm)
                  </th>
                  <th>
                    c<sub>u</sub> (kPa)
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>A</strong>
                  </td>
                  <td>Rock or rock-like formation, incl. ≤5&nbsp;m weaker surface layer</td>
                  <td>&gt; 800</td>
                  <td>—</td>
                  <td>—</td>
                </tr>
                <tr>
                  <td>
                    <strong>B</strong>
                  </td>
                  <td>Very dense sand/gravel or very stiff clay; properties generally increase with depth</td>
                  <td>360 – 800</td>
                  <td>&gt; 50</td>
                  <td>&gt; 250</td>
                </tr>
                <tr>
                  <td>
                    <strong>C</strong>
                  </td>
                  <td>Deep deposits of dense to medium-dense sand/gravel or stiff clay</td>
                  <td>180 – 360</td>
                  <td>15 – 50</td>
                  <td>70 – 250</td>
                </tr>
                <tr>
                  <td>
                    <strong>D</strong>
                  </td>
                  <td>Loose-to-medium cohesionless soils or predominantly soft-to-firm cohesive soils</td>
                  <td>&lt; 180</td>
                  <td>&lt; 15</td>
                  <td>&lt; 70</td>
                </tr>
                <tr>
                  <td>
                    <strong>E</strong>
                  </td>
                  <td>
                    Surface alluvium layer of type C or D, thickness ≈ 5–20&nbsp;m, underlain by stiffer material with v
                    <sub>s</sub>&nbsp;&gt;&nbsp;800&nbsp;m/s
                  </td>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                </tr>
                <tr>
                  <td>
                    <strong>
                      S<sub>1</sub>
                    </strong>
                  </td>
                  <td>Deposits including ≥10&nbsp;m of soft clays/silts with high PI (&gt;40) and high water content</td>
                  <td>&lt; 100 (indic.)</td>
                  <td>—</td>
                  <td>10 – 20</td>
                </tr>
                <tr>
                  <td>
                    <strong>
                      S<sub>2</sub>
                    </strong>
                  </td>
                  <td>
                    Liquefiable soils, sensitive clays, or profiles not included in A–E or S<sub>1</sub> (special studies)
                  </td>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2>Notes</h2>
          <ul>
            <li>
              Use VS₃₀ when shear-wave velocity data exist; otherwise rely on indicative N<sub>SPT</sub> / c<sub>u</sub>.
            </li>
            <li>
              S<sub>1</sub> and S<sub>2</sub> require project-specific evaluation; EC8 does not provide generic parameters for
              these.
            </li>
            <li>Where a National Annex is mandated, apply its modifications to ranges/wording as applicable.</li>
          </ul>
        </div>

        <div className="theoryButtonContainer">
          <Link to="/calculations/vs30SiteClassEc8">
            <Button variant="outlined" size="small">
              GO TO VS₃₀ CALCULATOR
            </Button>
          </Link>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Vs30TheoryEC8;
